import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Check, Crown, Star, Zap, Globe, MapPin } from 'lucide-react'
import { useSubscriptionStore, SubscriptionPlan } from '../stores/subscriptionStore'
import { useUserStore } from '../stores/userStore'
import { paymentService } from '../services/paymentService'
import { getLocationDisplayText, detectLocationFromBirthData } from '../utils/locationUtils'
import toast from 'react-hot-toast'

interface SubscriptionPlansProps {
  onClose?: () => void
  showUpgradePrompt?: boolean
}

const SubscriptionPlans: React.FC<SubscriptionPlansProps> = ({ onClose, showUpgradePrompt = false }) => {
  const { availablePlans, isSubscriptionActive, setUserLocation } = useSubscriptionStore()
  const { user } = useUserStore()
  const [isLoading, setIsLoading] = useState<string | null>(null)
  const [showEmailModal, setShowEmailModal] = useState(false)
  const [pendingPlan, setPendingPlan] = useState<SubscriptionPlan | null>(null)
  const [emailInput, setEmailInput] = useState<string>('')
  const [nameInput, setNameInput] = useState<string>('')
  const [locationDisplayText, setLocationDisplayText] = useState<string>('')

  // Detect user location on component mount
  useEffect(() => {
    if (user?.birthData) {
      const locationInfo = detectLocationFromBirthData(user.birthData)
      setLocationDisplayText(getLocationDisplayText(user.birthData))
      setUserLocation(locationInfo.currency)
    } else {
      // Try to get from localStorage if user store doesn't have birth data
      try {
        const localUser = JSON.parse(localStorage.getItem('user') || 'null')
        if (localUser?.birthData) {
          const locationInfo = detectLocationFromBirthData(localUser.birthData)
          setLocationDisplayText(getLocationDisplayText(localUser.birthData))
          setUserLocation(locationInfo.currency)
        }
      } catch (error) {
        console.error('Error parsing local user data:', error)
      }
    }
  }, [user?.birthData, setUserLocation])

  // Get location-based plans
  const getLocationBasedPlans = () => {
    try {
      console.log('getLocationBasedPlans called with user:', user)
      console.log('Available plans:', availablePlans)
      
      if (user?.birthData) {
        console.log('User has birth data:', user.birthData)
        return availablePlans.map(plan => {
          try {
            console.log('Getting location-based price for plan:', plan.id)
            const pricingInfo = plan.getLocationBasedPrice(user.birthData)
            console.log('Pricing info for plan', plan.id, ':', pricingInfo)
            return {
              ...plan,
              ...pricingInfo
            }
          } catch (error) {
            console.error('Error getting location-based price for plan:', plan.id, error)
            // Fallback to base plan if there's an error
            return plan
          }
        })
      } else {
        console.log('No user birth data available, returning base plans')
      }
      return availablePlans
    } catch (error) {
      console.error('Error in getLocationBasedPlans:', error)
      return availablePlans
    }
  }

  const handleSubscribe = async (plan: SubscriptionPlan) => {
    // Prefer profile values; if missing, try localStorage
    const localUser = (() => {
      try { return JSON.parse(localStorage.getItem('user') || 'null') } catch { return null }
    })()
    const candidateEmail = user?.email || localUser?.email || ''
    const candidateName = user?.name || localUser?.name || ''

    if (!candidateEmail || !candidateName) {
      setPendingPlan(plan)
      setEmailInput(candidateEmail)
      setNameInput(candidateName)
      setShowEmailModal(true)
      return
    }

    setIsLoading(plan.id)
    try {
      const response = await paymentService.initiatePayment(plan, candidateEmail, candidateName)
      if (!response.success) {
        toast.error(response.error || 'Failed to initiate payment')
      }
    } catch (error) {
      console.error('Payment error:', error)
      toast.error('Payment failed. Please try again.')
    } finally {
      setIsLoading(null)
    }
  }

  const confirmSubscribeWithEmail = async () => {
    if (!pendingPlan) return
    const email = emailInput.trim()
    const name = nameInput.trim() || 'Rraasi User'
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email')
      return
    }
    setShowEmailModal(false)
    setIsLoading(pendingPlan.id)
    try {
      const response = await paymentService.initiatePayment(pendingPlan, email, name)
      if (!response.success) {
        toast.error(response.error || 'Failed to initiate payment')
      }
    } catch (error) {
      console.error('Payment error:', error)
      toast.error('Payment failed. Please try again.')
    } finally {
      setIsLoading(null)
      setPendingPlan(null)
    }
  }

  const getPopularBadge = (plan: SubscriptionPlan) => {
    if (!plan.popular) return null
    
    return (
      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
        <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
          <Crown className="w-4 h-4" />
          Most Popular
        </div>
      </div>
    )
  }

  const getPlanIcon = (planId: string) => {
    switch (planId) {
      case 'starter_7':
        return <Zap className="w-6 h-6" />
      case 'premium_30':
        return <Star className="w-6 h-6" />
      case 'cosmic_90':
        return <Crown className="w-6 h-6" />
      case 'annual_365':
        return <Crown className="w-6 h-6" />
      default:
        return <Star className="w-6 h-6" />
    }
  }

  const getPlanGradient = (planId: string) => {
    switch (planId) {
      case 'starter_7':
        return 'from-blue-500 to-cyan-500'
      case 'premium_30':
        return 'from-purple-500 to-pink-500'
      case 'cosmic_90':
        return 'from-orange-500 to-red-500'
      case 'annual_365':
        return 'from-green-500 to-emerald-500'
      default:
        return 'from-gray-500 to-gray-600'
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-gray-900 rounded-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-white mb-2">
            {showUpgradePrompt ? 'Upgrade to Continue' : 'Choose Your Plan'}
          </h2>
          <p className="text-gray-400">
            {showUpgradePrompt 
              ? 'Unlock unlimited access to all features with our premium plans'
              : 'Select the perfect plan for your cosmic journey'
            }
          </p>
          
          {/* Location Indicator */}
          {locationDisplayText && (
            <div className="mt-4 p-3 bg-blue-500/20 border border-blue-500/30 rounded-lg">
              <div className="flex items-center justify-center gap-2 text-blue-300 text-sm">
                <Globe className="w-4 h-4" />
                <span>{locationDisplayText}</span>
              </div>
            </div>
          )}
          
          {!showUpgradePrompt && (
            <div className="mt-4 p-3 bg-green-500/20 border border-green-500/30 rounded-lg">
              <p className="text-green-300 text-sm">
                🎉 <strong>Free Tier:</strong> Try 20 free chat calls and 20 compatibility checks before upgrading!
              </p>
            </div>
          )}
        </div>

        {/* Subscription Plans */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
          {getLocationBasedPlans().map((plan) => (
            <motion.div
              key={plan.id}
              whileHover={{ y: -5 }}
              className={`relative rounded-2xl p-6 ${
                plan.popular
                  ? 'bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border-2 border-yellow-500/50'
                  : 'bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-gray-700/50'
              }`}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black px-4 py-1 rounded-full text-sm font-semibold">
                    ⭐ Most Popular
                  </div>
                </div>
              )}

              {/* Annual Pack Special Badge */}
              {plan.id === 'annual_365' && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <div className="bg-gradient-to-r from-purple-400 to-pink-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                    🎁 Best Value
                  </div>
                </div>
              )}

              {/* Plan Header */}
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
                <p className="text-gray-400 text-sm mb-4">{plan.description}</p>
                
                {/* RRAASI Coins Display */}
                <div className="mb-4 p-3 bg-gradient-to-r from-purple-600/20 to-blue-600/20 border border-purple-500/30 rounded-lg">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <div className="w-5 h-5 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs font-bold">🪙</span>
                    </div>
                    <span className="text-lg font-bold text-purple-300">
                      {plan.rraasiCoins.toLocaleString()} RRAASI Coins
                    </span>
                  </div>
                  <div className="text-xs text-purple-200">
                    Included with this plan
                  </div>
                </div>
                
                {/* Price Display */}
                <div className="mb-4">
                  <div className="text-3xl font-bold text-white">
                    {(plan as any).currencySymbol || '₹'}{plan.price}
                  </div>
                  <div className="text-gray-400 text-sm">
                    for {plan.duration} {plan.durationUnit}
                  </div>
                  
                  {/* Daily Price */}
                  <div className="text-xs text-gray-500 mt-1">
                    {(plan as any).currencySymbol || '₹'}{(plan.price / plan.duration).toFixed(2)} per day
                  </div>
                  
                  {/* Coin Value Ratio */}
                  <div className="mt-2 text-xs text-green-300">
                    💎 {((plan.rraasiCoins / plan.price) * 100).toFixed(0)} coins per ₹100
                  </div>
                  
                  {/* Savings Indicator for Annual Pack */}
                  {plan.id === 'annual_365' && (
                    <div className="mt-2 p-2 bg-green-500/20 border border-green-500/30 rounded-lg">
                      <div className="text-green-300 text-xs font-semibold">
                        💰 Save 25% vs Monthly
                      </div>
                    </div>
                  )}
                  
                  {/* Savings Indicator for Cosmic Pack */}
                  {plan.id === 'cosmic_90' && (
                    <div className="mt-2 p-2 bg-blue-500/20 border border-blue-500/30 rounded-lg">
                      <div className="text-blue-300 text-xs font-semibold">
                        💰 Save 17% vs Monthly
                      </div>
                    </div>
                  )}
                </div>

                {/* Show location info */}
                <div className="mt-2 text-xs text-gray-500 flex items-center justify-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {(plan as any).currency === 'USD' ? 'International' : 'India'}
                </div>
              </div>

              {/* Features List */}
              <div className="space-y-3 mb-6">
                {plan.features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300 text-sm">{feature}</span>
                  </div>
                ))}
              </div>

              {/* Action Button */}
              <button
                onClick={() => handleSubscribe(plan)}
                className={`w-full py-3 px-4 rounded-lg font-semibold transition-all duration-200 ${
                  plan.popular
                    ? 'bg-gradient-to-r from-yellow-400 to-orange-500 text-black hover:from-yellow-300 hover:to-orange-400'
                    : 'bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:from-blue-400 hover:to-purple-500'
                }`}
              >
                {isSubscriptionActive() ? 'Manage Subscription' : 'Subscribe Now'}
              </button>
            </motion.div>
          ))}
        </div>

        {/* Footer */}
        <div className="text-center text-gray-400 text-sm">
          <p>Secure payments powered by Razorpay</p>
          <p className="mt-2">
            Need help? Contact us at{' '}
            <a href="mailto:support@rraasi.com" className="text-purple-400 hover:text-purple-300">
              support@rraasi.com
            </a>
          </p>
        </div>

        {/* Close Button */}
        {onClose && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
          >
            ✕
          </button>
        )}
      </motion.div>

      {/* Email Prompt Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
          <div className="bg-gray-900 rounded-2xl p-6 w-full max-w-md border border-gray-700">
            <h3 className="text-white text-lg font-bold mb-4">Enter details to continue</h3>
            <div className="space-y-3">
              <input
                type="text"
                placeholder="Your Name"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white placeholder-gray-400"
              />
              <input
                type="email"
                placeholder="Email Address"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white placeholder-gray-400"
              />
            </div>
            <div className="flex gap-3 mt-5">
              <button
                onClick={() => setShowEmailModal(false)}
                className="flex-1 bg-gray-700 text-white py-2 rounded-xl hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={confirmSubscribeWithEmail}
                className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white py-2 rounded-xl hover:from-purple-600 hover:to-pink-600"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SubscriptionPlans 